import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Specialist } from 'src/features/chat/types';
import { apiGet, apiPost, apiPatch, apiDelete } from 'src/lib/api/axios';
import { toast } from 'src/components/ui/toast';
import { isAxiosError } from 'axios';
import useActiveTenantRole from './useActiveTenantRole';
import { useIsFreemiumUser } from './useIsFreemiumUser';

/** Payload for create/update — uses the frontend KnowledgeBase shape (with PinnedDocumentRef[]).
 * The hook converts document refs to IDs before sending to the API. */
type SpecialistPayload = Pick<Specialist, 'name' | 'colour' | 'prompt_parts' | 'knowledge_base' | 'tool_set'>;

interface UseSpecialistsManagementReturn {
  specialists: Specialist[];
  setSpecialists: (specialists: Specialist[]) => void;
  loading: boolean;
  fetchSpecialists: () => Promise<void>;
  addSpecialist: (specialist: SpecialistPayload) => Promise<Specialist | null>;
  updateSpecialist: (id: string, specialist: Partial<SpecialistPayload>) => Promise<Specialist | null>;
  deleteSpecialist: (id: string) => Promise<boolean>;
}

const SPECIALISTS_QUERY_KEY = ['specialists'];

/** Convert KnowledgeBase (with PinnedDocumentRef[]) → write payload (IDs only).
 * The backend KnowledgeBaseInput accepts documents as string IDs, not objects. */
function toApiPayload(data: SpecialistPayload | Partial<SpecialistPayload>): unknown {
  if (!data.knowledge_base) return data;
  return {
    ...data,
    knowledge_base: {
      ...data.knowledge_base,
      documents: data.knowledge_base.documents?.map((d) => d.id)
    }
  };
}

export const useSpecialistsManagement = (): UseSpecialistsManagementReturn => {
  const { isStandardMember } = useActiveTenantRole();
  const isFreemium = useIsFreemiumUser();
  const queryClient = useQueryClient();

  const enabled = isStandardMember && !isFreemium;

  const { data: specialists = [], isLoading: loading } = useQuery({
    queryKey: SPECIALISTS_QUERY_KEY,
    queryFn: async (): Promise<Specialist[]> => {
      const response = await apiGet<{ specialists: Specialist[]; total: number }>('/specialists/?include_deleted=true');
      return response.data.specialists;
    },
    enabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false
  });

  const setSpecialists = useCallback(
    (newSpecialists: Specialist[]) => {
      queryClient.setQueryData(SPECIALISTS_QUERY_KEY, newSpecialists);
    },
    [queryClient]
  );

  const fetchSpecialists = useCallback(async () => {
    if (!enabled) return;
    await queryClient.invalidateQueries({ queryKey: SPECIALISTS_QUERY_KEY });
  }, [enabled, queryClient]);

  const getErrorMessage = useCallback((error: unknown, fallback: string) => {
    if (isAxiosError(error)) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string' && detail.trim().length > 0) {
        return detail;
      }
    }
    return fallback;
  }, []);

  const addSpecialist = useCallback(
    async (specialistData: SpecialistPayload): Promise<Specialist | null> => {
      try {
        const response = await apiPost<Specialist>('/specialists/', toApiPayload(specialistData));
        const newSpecialist = response.data;
        queryClient.setQueryData(SPECIALISTS_QUERY_KEY, (prev: Specialist[] = []) => [...prev, newSpecialist]);
        toast.success('Specialist added successfully');
        return newSpecialist;
      } catch (error) {
        const message = getErrorMessage(error, 'Failed to add specialist');
        console.error('Error adding specialist:', error);
        throw new Error(message);
      }
    },
    [queryClient, getErrorMessage]
  );

  const updateSpecialist = useCallback(
    async (id: string, updates: Partial<SpecialistPayload>): Promise<Specialist | null> => {
      try {
        const response = await apiPatch<Specialist>(`/specialists/${id}`, toApiPayload(updates));
        const updatedSpecialist = response.data;
        queryClient.setQueryData(SPECIALISTS_QUERY_KEY, (prev: Specialist[] = []) =>
          prev.map((spec) => (spec.id === id ? updatedSpecialist : spec))
        );
        toast.success('Specialist updated successfully');
        return updatedSpecialist;
      } catch (error) {
        const message = getErrorMessage(error, 'Failed to update specialist');
        console.error('Error updating specialist:', error);
        throw new Error(message);
      }
    },
    [queryClient, getErrorMessage]
  );

  const deleteSpecialist = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await apiDelete(`/specialists/${id}`);
        queryClient.setQueryData(SPECIALISTS_QUERY_KEY, (prev: Specialist[] = []) =>
          prev.filter((spec) => spec.id !== id)
        );
        toast.success('Specialist deleted successfully');
        return true;
      } catch (error) {
        toast.error('Failed to delete specialist');
        console.error('Error deleting specialist:', error);
        return false;
      }
    },
    [queryClient]
  );

  return {
    specialists,
    setSpecialists,
    loading,
    fetchSpecialists,
    addSpecialist,
    updateSpecialist,
    deleteSpecialist
  };
};
